import { useEffect } from "react";
import { X } from "lucide-react";
import { toEmbedUrl } from "@/data/projectsAdapter";

/**
 * Cinematic YouTube/Vimeo lightbox.
 * Props: open (bool), onClose (fn), videoUrl (string), title (string, optional)
 */
const VideoModal = ({ open, onClose, videoUrl, title }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !videoUrl) return null;

  const embed = toEmbedUrl(videoUrl);
  // Add autoplay + modestbranding for a nicer cinema-feel opening
  const src = embed.includes("?")
    ? `${embed}&autoplay=1&rel=0&modestbranding=1`
    : `${embed}?autoplay=1&rel=0&modestbranding=1`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || "Video"}
      data-testid="video-modal"
      className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-300"
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close"
        data-testid="video-modal-close"
        onClick={onClose}
        className="absolute top-5 right-5 sm:top-8 sm:right-8 w-11 h-11 grid place-items-center rounded-full border border-white/20 text-white/80 hover:text-[#E6A95B] hover:border-[#E6A95B] transition"
      >
        <X className="w-5 h-5" />
      </button>
      <div
        className="relative w-full max-w-6xl aspect-video rounded-2xl overflow-hidden border border-[#E6A95B]/25 shadow-[0_30px_120px_-20px_rgba(230,169,91,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          data-testid="video-modal-iframe"
          src={src}
          title={title || "ARTLENSS Video"}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
};

export default VideoModal;
